import { Box, Button } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import BasicSearchField from "../../shared/BasicSearchField";
import BasicSelect from "../../shared/BasicSelect";
import AdhocAgreementTablePagination from "./table/Pagination";
import { AdhocProjectsTable } from "./table/Table";

import adhocAgreementsService from "../../../services/AdhocAgreementsService";
import NewAdhocProjectForm from "../new/Form";
import { AdhocProject } from "integrity-owls-logic/dist";

interface AdhocProjectsContentProps {
  onError: (error: string) => void;
  onSuccess: () => void;
}

export const AdhocProjectsContent: React.FC<AdhocProjectsContentProps> = ({
  onError,
  onSuccess
}) => {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<string | number>(5);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchProjects = useCallback(async () => {
    setLoading(true);

    const projectsResponse = await adhocAgreementsService.getAdhocProjects({
      page,
      rowsPerPage: typeof rowsPerPage === "string" ? -1 : rowsPerPage,
      searchText
    });
    console.log("projectsResponse", projectsResponse);
    if (projectsResponse) {
      setProjects(projectsResponse.data);
      setTotalRecords(projectsResponse.total);
    }
    setLoading(false);
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    fetchProjects();
  }, [rowsPerPage, page]);

  const handleToggleStatus = useCallback(async (project: AdhocProject) => {
    await adhocAgreementsService.toggleAdhocProjectStatus({ id: project.id });
    fetchProjects();
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: string) => {
    if (rowsPerPage === "All") {
      setRowsPerPage("All");
    } else {
      setRowsPerPage(parseInt(rowsPerPage));
    }
  }, []);

  const changePage = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [page]
  );

  return (
    <Box
      display="flex"
      style={{ width: "100%" }}
      p={2}
      pt={1}
      flexDirection="row"
      justifyContent="space-between"
    >
      <Box flexDirection="column" flex={4}>
        <Box flexDirection="row" display="flex" width="100%">
          <Box flex={5}>
            <BasicSearchField
              label="Search by ID or project name..."
              searchText={searchText}
              handleFieldChange={e => setSearchText(e.currentTarget.value)}
              handleKeyPress={e => e.key === "Enter" && fetchProjects()}
            />
          </Box>
          <Box
            flex={1}
            ml={2}
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => fetchProjects()}
            >
              Search
            </Button>
          </Box>
          <Box flex={4} display="flex" justifyContent="flex-end">
            <BasicSelect
              options={["5", "10", "25", "50", "All"]}
              value={rowsPerPage}
              handleChange={e => handleRowsPerPageChange(e)}
            />
          </Box>
        </Box>

        <Box flexDirection="row" display="flex" width="100%">
          <AdhocProjectsTable
            projects={projects}
            loading={loading}
            handleToggleStatus={handleToggleStatus}
          />
        </Box>

        <Box flexDirection="row" display="flex" width="100%">
          <AdhocAgreementTablePagination
            page={page}
            changePage={changePage}
            rowsPerPage={
              rowsPerPage === "All" ? totalRecords : (rowsPerPage as number)
            }
            totalRecords={totalRecords}
          />
        </Box>
      </Box>
      <NewAdhocProjectForm
        onError={onError}
        onSuccess={() => {
          onSuccess();
          fetchProjects();
        }}
      />
    </Box>
  );
};

export default AdhocProjectsContent;
